import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Input,
  Row,
  Spinner,
  Table,
  Card,
  CardBody,
} from "reactstrap";
import Select from "react-select";
import PlanillaPageLayout from "./PlanillaPageLayout";
import { requestJson, resolveApiUrl } from "../../helpers/api";

type PlanillaRow = Record<string, unknown>;

const formatMonto = (valor: number) =>
  `Gs. ${valor.toLocaleString("es-PY", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const leerNumeroDesdeFila = (fila: PlanillaRow | null | undefined, campo = "LiquidacionId") => {
  if (!fila) {
    return 0;
  }
  const valor = fila[campo];
  if (typeof valor === "number" && Number.isFinite(valor)) {
    return valor;
  }
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : 0;
};

const obtenerTexto = (fila: PlanillaRow | null | undefined, campo: string, placeholder = "—") => {
  if (!fila) {
    return placeholder;
  }
  const valor = fila[campo];
  if (valor === null || valor === undefined || valor === "") {
    return placeholder;
  }
  return String(valor);
};

const RRHHLiquidacionSalario = () => {
  const [filas, setFilas] = useState<PlanillaRow[]>([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState("");
  const [filtrosPeriodo, setFiltrosPeriodo] = useState<string[]>([]);
  const [filtrosSucursal, setFiltrosSucursal] = useState<string[]>([]);
  const [filtrosEmpresa, setFiltrosEmpresa] = useState<string[]>([]);
  const [pagina, setPagina] = useState(0);
  const [filasPorPagina, setFilasPorPagina] = useState(5);
  const [seleccionados, setSeleccionados] = useState<Set<number>>(new Set());
  const cargarPlanilla = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const data = await requestJson<{ ok: boolean; filas: PlanillaRow[] }>("/api/rrhh/planillas");
      const registros = Array.isArray(data?.filas) ? data.filas : [];
      setFilas(registros);
      setSeleccionados(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudieron obtener las liquidaciones.");
      setFilas([]);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargarPlanilla();
  }, [cargarPlanilla]);

  const periodosDisponibles = useMemo(() => {
    const valores = new Set<string>();
    filas.forEach((fila) => {
      const valor = obtenerTexto(fila, "Mes", "").trim();
      if (valor) {
        valores.add(valor);
      }
    });
    return Array.from(valores).sort((a, b) => a.localeCompare(b));
  }, [filas]);

  const sucursalesDisponibles = useMemo(() => {
    const valores = new Set<string>();
    filas.forEach((fila) => {
      const valor = obtenerTexto(fila, "Sucursal/ Sector", "").trim();
      if (valor) {
        valores.add(valor);
      }
    });
    return Array.from(valores).sort((a, b) => a.localeCompare(b));
  }, [filas]);

  const empresasDisponibles = useMemo(() => {
    const valores = new Set<string>();
    filas.forEach((fila) => {
      const valor = obtenerTexto(fila, "Empresa", "").trim();
      if (valor) {
        valores.add(valor);
      }
    });
    return Array.from(valores).sort((a, b) => a.localeCompare(b));
  }, [filas]);

  const filasFiltradas = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();
    const esNumero = /^[0-9.]+$/.test(termino);
    return filas.filter((fila) => {
      if (termino) {
        const nombre = (fila["Apellidos, Nombres"] ?? "").toString().toLowerCase();
        const ci = (fila["CI N°"] ?? "").toString().toLowerCase();
        const ruc = (fila["RUC"] ?? "").toString().toLowerCase();
        const empresa = (fila["Empresa"] ?? "").toString().toLowerCase();
        if (esNumero) {
          const normalizado = termino.replace(/[^0-9]/g, "");
          const ciNormalizado = ci.replace(/[^0-9]/g, "");
          const rucNormalizado = ruc.replace(/[^0-9]/g, "");
          if (!ciNormalizado.includes(normalizado) && !rucNormalizado.includes(normalizado)) {
            return false;
          }
        } else if (!nombre.includes(termino) && !empresa.includes(termino)) {
          return false;
        }
      }
      if (filtrosPeriodo.length > 0) {
        const periodo = obtenerTexto(fila, "Mes", "").trim();
        if (!filtrosPeriodo.includes(periodo)) {
          return false;
        }
      }
      if (filtrosSucursal.length > 0) {
        const sucursal = obtenerTexto(fila, "Sucursal/ Sector", "").trim();
        if (!filtrosSucursal.includes(sucursal)) {
          return false;
        }
      }
      if (filtrosEmpresa.length > 0) {
        const empresa = obtenerTexto(fila, "Empresa", "").trim();
        if (!filtrosEmpresa.includes(empresa)) {
          return false;
        }
      }
      return true;
    });
  }, [busqueda, filas, filtrosPeriodo, filtrosSucursal, filtrosEmpresa]);

  const totalPaginas = Math.max(1, Math.ceil(filasFiltradas.length / filasPorPagina));

  useEffect(() => {
    setPagina((prev) => Math.min(prev, totalPaginas - 1));
  }, [totalPaginas]);

  useEffect(() => {
    const idsValidos = new Set(filas.map((fila) => leerNumeroDesdeFila(fila)));
    setSeleccionados((prev) => {
      const nuevo = new Set<number>();
      prev.forEach((id) => {
        if (idsValidos.has(id)) {
          nuevo.add(id);
        }
      });
      return nuevo;
    });
  }, [filas]);

  const filasPagina = useMemo(() => {
    const inicio = pagina * filasPorPagina;
    return filasFiltradas.slice(inicio, inicio + filasPorPagina);
  }, [pagina, filasFiltradas, filasPorPagina]);

  const toggleSeleccion = (id: number) => {
    if (!id) {
      return;
    }
    setSeleccionados((prev) => {
      const nuevo = new Set(prev);
      if (nuevo.has(id)) {
        nuevo.delete(id);
      } else {
        nuevo.add(id);
      }
      return nuevo;
    });
  };

  const toggleSeleccionPagina = () => {
    const idsPagina = filasPagina.map((fila) => leerNumeroDesdeFila(fila)).filter((id) => id > 0);
    const todosSeleccionados = idsPagina.every((id) => seleccionados.has(id));
    setSeleccionados((prev) => {
      const nuevo = new Set(prev);
      if (todosSeleccionados) {
        idsPagina.forEach((id) => nuevo.delete(id));
      } else {
        idsPagina.forEach((id) => nuevo.add(id));
      }
      return nuevo;
    });
  };

  const obtenerIdsMasivos = () => {
    let ids = Array.from(seleccionados).filter((id) => id > 0);
    if (ids.length === 0) {
      ids = filasFiltradas.map((fila) => leerNumeroDesdeFila(fila)).filter((id) => id > 0);
    }
    return ids;
  };

  const abrirVentana = useCallback((ruta: string) => {
    const url = resolveApiUrl(ruta);
    const abierta = window.open(url, "_blank", "noopener");
    if (!abierta) {
      window.alert("Permití las ventanas emergentes para continuar con la impresión.");
    }
  }, []);

  const solicitarEnlaceImpresion = useCallback(
    async (tipo: "masivo" | "individual", modo: "print" | "pdf", ids?: number[]) => {
      return requestJson<{ url: string }>("/api/rrhh/liquidaciones/impresion-link", {
        method: "POST",
        body: JSON.stringify({ tipo, modo, ids }),
      });
    },
    [],
  );

  const manejarAccionMasiva = async (modo: "print" | "pdf") => {
    const ids = obtenerIdsMasivos();
    try {
      const respuesta = await solicitarEnlaceImpresion("masivo", modo, ids);
      abrirVentana(respuesta.url);
    } catch (err) {
      const mensaje =
        err instanceof Error ? err.message : "No se pudo generar el enlace de impresión.";
      window.alert(mensaje);
    }
  };

  const manejarAccionIndividual = async (modo: "print" | "pdf", id: number) => {
    if (!id) {
      return;
    }
    try {
      const respuesta = await solicitarEnlaceImpresion("individual", modo, [id]);
      abrirVentana(respuesta.url);
    } catch (err) {
      const mensaje =
        err instanceof Error ? err.message : "No se pudo generar el enlace de impresión.";
      window.alert(mensaje);
    }
  };

  type OpcionSelect = { label: string; value: string };

  const construirValorSelect = (valores: string[]): OpcionSelect[] =>
    valores.map((valor) => ({
      label: valor || "Sin dato",
      value: valor,
    }));

  const manejarCambioSelect = (
    seleccion: readonly OpcionSelect[],
    setter: (values: string[]) => void,
  ) => {
    setter(seleccion.map((option) => option.value));
    setPagina(0);
  };

  return (
    <PlanillaPageLayout
      title="Liquidación de salario"
     
    >
      {error ? (
        <Alert color="danger" toggle={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      <Card className="mb-3">
        <CardBody className="pb-2">
          <Row className="align-items-center g-3">
            <Col lg={4}>
              <h4 className="mb-1">Liquidación de salario</h4>
              <p className="text-muted mb-0">
                Controlá y filtrá las liquidaciones disponibles; seleccioná recibos para generar impresiones o PDFs según lo necesites.
              </p>
            </Col>
            <Col lg={8}>
              <div className="d-flex flex-wrap gap-2 justify-content-lg-end text-muted small">
                Esta vista está pensada para revisar, imprimir o exportar los recibos cargados.
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>
      <div className="mb-3">
        <Card>
          <CardBody>
            <div className="d-flex flex-wrap gap-3 align-items-end">
              <div className="flex-grow-1">
                <Input
                  type="search"
                  placeholder="Buscar por CI, nombre, RUC o empresa"
                  value={busqueda}
                  onChange={(event) => {
                    setBusqueda(event.target.value);
                    setPagina(0);
                  }}
                />
              </div>
              <div className="text-nowrap min-width-0" style={{ minWidth: 200 }}>
                <p className="text-muted mb-1 small">Período</p>
                <Select
                  isMulti
                  classNamePrefix="select"
                  placeholder="Todos los períodos"
                  value={construirValorSelect(filtrosPeriodo)}
                  onChange={(selected: readonly OpcionSelect[] | null) =>
                    manejarCambioSelect(selected ?? [], setFiltrosPeriodo)
                  }
                  options={construirValorSelect(periodosDisponibles)}
                  noOptionsMessage={() => "Sin datos"}
                />
              </div>
              <div className="text-nowrap">
                <p className="text-muted mb-1 small">Sucursal</p>
                <Select
                  isMulti
                  classNamePrefix="select"
                  placeholder="Todas las sucursales"
                  value={construirValorSelect(filtrosSucursal)}
                  onChange={(selected: readonly OpcionSelect[] | null) =>
                    manejarCambioSelect(selected ?? [], setFiltrosSucursal)
                  }
                  options={construirValorSelect(sucursalesDisponibles)}
                  noOptionsMessage={() => "Sin datos"}
                />
              </div>
              <div className="text-nowrap">
                <p className="text-muted mb-1 small">Empresa</p>
                <Select
                  isMulti
                  classNamePrefix="select"
                  placeholder="Todas las empresas"
                  value={construirValorSelect(filtrosEmpresa)}
                  onChange={(selected: readonly OpcionSelect[] | null) =>
                    manejarCambioSelect(selected ?? [], setFiltrosEmpresa)
                  }
                  options={construirValorSelect(empresasDisponibles)}
                  noOptionsMessage={() => "Sin datos"}
                />
              </div>
              <div className="ms-auto text-end">
                <Button
                  color="link"
                  className="p-0"
                  onClick={() => {
                    setFiltrosEmpresa([]);
                    setFiltrosPeriodo([]);
                    setFiltrosSucursal([]);
                    setPagina(0);
                  }}
                >
                  Limpiar filtros
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
      <Card className="h-100">
        <CardBody className="d-flex flex-column">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <div className="d-flex flex-wrap align-items-center gap-2 flex-grow-1">
                  <Input
                    type="select"
                    value={filasPorPagina}
                    onChange={(event) => {
                      setFilasPorPagina(Number(event.target.value) || 5);
                      setPagina(0);
                    }}
                    className="w-auto"
                  >
                    {[5, 10, 25, 50].map((cantidad) => (
                      <option key={cantidad} value={cantidad}>
                        {cantidad} por página
                      </option>
                    ))}
                  </Input>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <Button color="soft-secondary" onClick={() => void cargarPlanilla()}>
                    <i className="ri-refresh-line me-1" />
                    Actualizar
                  </Button>
                  <Button color="soft-info" onClick={() => void manejarAccionMasiva("pdf")}>
                    <i className="ri-file-download-line me-1" />
                    PDF masivo
                  </Button>
                  <Button color="soft-primary" onClick={() => void manejarAccionMasiva("print")}>
                    <i className="ri-printer-line me-1" />
                    Imprimir masivo
                  </Button>
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center text-muted small mb-2">
                <span>
                  {filasFiltradas.length} registros · Página {pagina + 1} de {totalPaginas}
                </span>
                {cargando ? (
                  <span className="d-flex align-items-center gap-2">
                    <Spinner size="sm" /> Cargando...
                  </span>
                ) : null}
              </div>
              <div className="table-responsive flex-grow-1">
                <Table hover responsive className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 50 }}>
                        <div className="form-check">
                          <Input
                            type="checkbox"
                            className="form-check-input"
                            checked={
                              filasPagina.length > 0 &&
                              filasPagina.every((fila) => seleccionados.has(leerNumeroDesdeFila(fila)))
                            }
                            onChange={toggleSeleccionPagina}
                          />
                        </div>
                      </th>
                      <th>CI / Colab.</th>
                      <th>Empresa</th>
                      <th>Puesto~Suc.</th>
                      <th className="text-end">Ingresos</th>
                      <th className="text-end">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filasPagina.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4">
                          No hay registros para mostrar.
                        </td>
                      </tr>
                    ) : (
                      filasPagina.map((fila) => {
                        const id = leerNumeroDesdeFila(fila);
                        const seleccionado = seleccionados.has(id);
                        return (
                          <tr key={id || Math.random()}>
                            <td>
                              <div className="form-check mb-0">
                                <Input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={seleccionado}
                                  onChange={(event) => {
                                    event.stopPropagation();
                                    toggleSeleccion(id);
                                  }}
                                />
                              </div>
                            </td>
                            <td>
                              <div className="fw-semibold">{obtenerTexto(fila, "Apellidos, Nombres")}</div>
                              <div className="text-muted small">
                                CI {obtenerTexto(fila, "CI N°")} · ID #{leerNumeroDesdeFila(fila)} ·{" "}
                                {obtenerTexto(fila, "Mes")}
                              </div>
                            </td>
                            <td>
                              <div>{obtenerTexto(fila, "Empresa")}</div>
                              <div className="text-muted small">{obtenerTexto(fila, "RUC", "Sin RUC")}</div>
                            </td>
                            <td>
                              <div>{obtenerTexto(fila, "Puesto")}</div>
                              <div className="text-muted small">{obtenerTexto(fila, "Sucursal/ Sector")}</div>
                            </td>
                            <td className="text-end">
                              <span
                                className={`badge fw-semibold ${
                                  seleccionado ? "bg-soft-primary text-primary" : "bg-secondary text-light"
                                }`}
                              >
                                {formatMonto(Number(fila["TOTAL INGRESOS"] ?? 0))}
                              </span>
                            </td>
                            <td className="text-end">
                              <div className="d-inline-flex gap-2">
                                <Button
                                  color="soft-info"
                                  size="sm"
                                  onClick={() => void manejarAccionIndividual("pdf", id)}
                                  title="Descargar PDF"
                                >
                                  <i className="ri-download-line" />
                                </Button>
                                <Button
                                  color="soft-primary"
                                  size="sm"
                                  onClick={() => void manejarAccionIndividual("print", id)}
                                  title="Imprimir"
                                >
                                  <i className="ri-printer-line" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
              <div className="d-flex justify-content-between align-items-center text-muted small mt-3">
                <div>
                  Mostrando {filasPagina.length === 0 ? 0 : pagina * filasPorPagina + 1}-
                  {pagina * filasPorPagina + filasPagina.length} de {filasFiltradas.length} filas filtradas.
                </div>
                <div className="d-flex gap-2">
                  <Button
                    color="light"
                    size="sm"
                    disabled={pagina === 0}
                    onClick={() => setPagina((prev) => Math.max(0, prev - 1))}
                  >
                    <i className="ri-arrow-left-line me-1" />
                    Anterior
                  </Button>
                  <Button
                    color="light"
                    size="sm"
                    disabled={pagina + 1 >= totalPaginas}
                    onClick={() => setPagina((prev) => Math.min(totalPaginas - 1, prev + 1))}
                  >
                    Siguiente
                    <i className="ri-arrow-right-line ms-1" />
                  </Button>
                </div>
              </div>
        </CardBody>
      </Card>
    </PlanillaPageLayout>
  );
};

export default RRHHLiquidacionSalario;
